import { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapSpot, MapCategoryFilter } from '@/types/spots';
import MapOverlay from '@/components/spots-map/MapOverlay';
import SpotBottomSheet from '@/components/spots-map/SpotBottomSheet';
import SuggestSpotModal from '@/components/spots-map/SuggestSpotModal';
import SpotsMapEmailGate from '@/components/spots-map/SpotsMapEmailGate';
import { Plus, Loader2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiemFpbmg0MiIsImEiOiJjbTR1NHBoYjMwZXQ4MmtzYWIwZzJzaWR6In0.gE5QluhHA_G_TzvGpOsRdg';
const API_BASE = 'https://spotsvc.up.railway.app';

const POD_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="2" width="12" height="20" rx="2"/><rect x="10" y="6" width="4" height="14" rx="1"/><line x1="6" y1="6" x2="18" y2="6"/></svg>`;

const CATEGORY_EMOJIS: Record<string, string> = {
  cafe: '☕',
  gym: '🏋️',
  hotel_lobby: '🏨',
  coworking: '💻',
  library: '📚',
  restaurant: '🍽️',
  other: '✨',
};

const SpotsMap = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);

  const [allSpots, setAllSpots] = useState<MapSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<MapCategoryFilter>('all');
  const [selectedSpot, setSelectedSpot] = useState<MapSpot | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [showEmailGate, setShowEmailGate] = useState(() => !localStorage.getItem('spotsMapEmail'));
  const [showTooltip, setShowTooltip] = useState(true);

  // Fetch all spots from API (paginated, max 100 per page)
  useEffect(() => {
    const fetchAllSpots = async () => {
      setLoading(true);
      try {
        let page = 1;
        let allFetched: MapSpot[] = [];
        let hasMore = true;
        while (hasMore) {
          const res = await fetch(`${API_BASE}/admin/spots?page=${page}&page_size=100`);
          if (!res.ok) throw new Error('Failed to fetch');
          const data = await res.json();
          allFetched = [...allFetched, ...(data.spots || [])];
          hasMore = allFetched.length < data.total;
          page++;
        }
        setAllSpots(allFetched.filter(s => s.is_active));
      } catch (e) {
        console.error('Failed to load spots:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAllSpots();
  }, []);

  // Auto-dismiss tooltip after 6 seconds
  useEffect(() => {
    if (!showTooltip) return;
    const t = setTimeout(() => setShowTooltip(false), 6000);
    return () => clearTimeout(t);
  }, [showTooltip]);

  const filteredSpots = selectedCategory === 'all'
    ? allSpots
    : allSpots.filter(s => s.category === selectedCategory);

  // Only spots with coordinates can be shown on map
  const mappableSpots = filteredSpots.filter(s => s.latitude != null && s.longitude != null);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-0.0935, 51.5164],
      zoom: 12,
      dragRotate: false,
      touchZoomRotate: true,
      pitchWithRotate: false,
    });

    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-left');

    return () => {
      markersRef.current.forEach(m => m.remove());
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Update markers
  useEffect(() => {
    if (!map.current) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    for (const spot of mappableSpots) {
      const isSelected = selectedSpot?.id === spot.id;
      const isPod = spot.category === 'pod';
      const emoji = CATEGORY_EMOJIS[spot.category] || '📍';

      const el = document.createElement('div');
      el.style.cssText = `
        display:flex; align-items:center; justify-content:center;
        width:${isSelected ? '44px' : '38px'}; height:${isSelected ? '44px' : '38px'};
        background:${isSelected ? 'hsl(158, 33%, 18%)' : 'white'};
        border-radius:50%;
        box-shadow:0 2px 8px rgba(0,0,0,0.18);
        cursor:pointer;
        font-size:${isSelected ? '20px' : '17px'};
        color:${isSelected ? 'white' : 'hsl(158, 33%, 18%)'};
        border:${isSelected ? '3px solid white' : '2px solid rgba(0,0,0,0.06)'};
      `;
      if (isPod) {
        el.innerHTML = POD_ICON_SVG;
      } else {
        el.textContent = emoji;
      }

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelectedSpot(spot);
      });

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([spot.longitude!, spot.latitude!])
        .addTo(map.current!);

      markersRef.current.push(marker);
    }
  }, [mappableSpots, selectedSpot]);

  // Pan to selected spot
  useEffect(() => {
    if (!map.current || !selectedSpot?.latitude || !selectedSpot?.longitude) return;
    map.current.easeTo({ center: [selectedSpot.longitude, selectedSpot.latitude], zoom: 14, duration: 600 });
  }, [selectedSpot]);

  const handleSearch = async (query: string) => {
    if (!query.trim() || !map.current) return;
    try {
      const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=GB&limit=1`);
      const data = await res.json();
      if (data.features?.[0]) {
        const [lng, lat] = data.features[0].center;
        map.current.flyTo({ center: [lng, lat], zoom: 14, duration: 1500 });
      }
    } catch (e) {
      console.error('Search error:', e);
    }
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (map.current) {
          if (userMarkerRef.current) {
            userMarkerRef.current.setLngLat([longitude, latitude]);
          } else {
            const el = document.createElement('div');
            el.style.cssText = 'width:14px;height:14px;background:#3b82f6;border-radius:50%;border:3px solid white;box-shadow:0 0 0 2px rgba(59,130,246,0.3);';
            userMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
              .setLngLat([longitude, latitude])
              .addTo(map.current);
          }
          map.current.flyTo({ center: [longitude, latitude], zoom: 14, duration: 1500 });
        }
        setIsLocating(false);
      },
      () => setIsLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // No longer needed — SuggestSpotModal now navigates to /spots/suggest

  return (
    <div className="h-[100dvh] w-full relative overflow-hidden">
      <div ref={mapContainer} className="absolute inset-0" />

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {showEmailGate && (
        <SpotsMapEmailGate onSubmit={() => setShowEmailGate(false)} />
      )}

      <MapOverlay
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onSearch={handleSearch}
        onUseLocation={handleUseLocation}
        isLocating={isLocating}
        spotCount={filteredSpots.length}
        spots={filteredSpots}
        onSelectSpot={setSelectedSpot}
      />

      {/* FABs */}
      <div className="fixed right-4 bottom-[180px] z-40 flex flex-col items-end gap-3 laptop:bottom-6 desktop:bottom-6">
        <button
          onClick={handleUseLocation}
          disabled={isLocating}
          className="w-14 h-14 rounded-full bg-background text-foreground shadow-lg flex items-center justify-center hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 border border-border"
          aria-label="Use my location"
        >
          {isLocating ? (
            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="12" /></svg>
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
          )}
        </button>
        <div className="relative flex items-center">
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, x: 8, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 8, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="absolute right-16 bg-foreground text-background text-xs font-medium px-3 py-2 rounded-xl shadow-lg whitespace-nowrap text-center leading-snug"
              >
                Know a great spot? Suggest one here 👉
                <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2.5 h-2.5 bg-foreground rotate-45 rounded-sm" />
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={() => { setIsSuggestOpen(true); setShowTooltip(false); }}
            className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:opacity-90 active:scale-95 transition-all"
            aria-label="Suggest a spot"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </div>

      <SpotBottomSheet
        spots={filteredSpots}
        selectedSpot={selectedSpot}
        onSelectSpot={setSelectedSpot}
        onClose={() => setSelectedSpot(null)}
      />

      <SuggestSpotModal
        isOpen={isSuggestOpen}
        onClose={() => setIsSuggestOpen(false)}
      />
    </div>
  );
};

export default SpotsMap;
